import {
  default as React,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { Octokit } from '@octokit/rest';
import { DocsHelpIcon } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

interface Owner {
  entityRef: string;
  name: string;
  displayName: string;
  type: 'team' | 'user';
  members?: string[]; // GitHub usernames (only for teams)
}

export const TeamPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const config = useApi(configApiRef);
  const [selectedOwners, setSelectedOwners] = useState<Owner[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [loadingTeamsFromGithub, setLoadingTeamsFromGithub] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const [githubError, setGithubError] = useState<string>('');
  const [samlAuthUrl, setSamlAuthUrl] = useState<string | null>(null);
  const [requiredError, setRequiredError] = useState<string>('');
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const previousRepoTypeRef = useRef<string | undefined>(undefined);
  const previousOrgRef = useRef<string | undefined>(undefined);
  const previousRepoTypeForTeamLoadRef = useRef<string | undefined>(undefined);
  const samlTimerRef = useRef<number | null>(null);
  const samlPopupRef = useRef<Window | null>(null);

  // Get repoType from form context to determine if we should show users
  const repoType = formContext?.formData?.repoType;

  // LOCAL DEVELOPMENT ONLY (IDP-1050): when a local dev PAT is configured, load teams
  // from the Backstage catalog (Groups synced from the GitHub org) instead of calling
  // api.github.com directly from the browser. Direct browser calls fail locally (org
  // SAML enforcement / token rejection), even when the PAT is valid server-side.
  //
  // github.localDevToken is only ever set in the gitignored app-config.local.yaml
  // (see config.d.ts) — never in deployed config — so its presence is the "local mode"
  // signal and deployed dev/prod always use the live GitHub team listing below.
  const isLocalDev = Boolean(config.getOptionalString('github.localDevToken'));

  // Clear/filter selected owners when repo type changes
  useEffect(() => {
    // Only run when repoType actually changes (not on first mount)
    if (
      previousRepoTypeRef.current !== undefined &&
      previousRepoTypeRef.current !== repoType
    ) {
      if (repoType === 'Standard' && selectedOwners.length > 0) {
        // Filter out any users when switching to Standard
        const onlyTeams = selectedOwners.filter(owner => owner.type === 'team');
        if (onlyTeams.length !== selectedOwners.length) {
          // Users were removed
          setSelectedOwners(onlyTeams);
          setValidationError('');
          // Update form with only teams
          if (onlyTeams.length === 0) {
            onChange('');
          } else {
            const ownersData = onlyTeams.map(owner => ({
              team: owner.entityRef,
            }));
            onChange(JSON.stringify(ownersData));
          }
        }
      } else {
        // Playground - clear any validation error
        setValidationError('');
      }
    }
    // Update the ref for next comparison
    previousRepoTypeRef.current = repoType;
  }, [repoType, selectedOwners, onChange]);

  // Note: SAML authorization is now handled in GithubSsoLogin component
  // When organization changes, a fresh token with SAML authorization is obtained automatically

  // Helper function to load teams from GitHub API for selected org
  const loadTeamsFromGithub = useCallback(
    async (org: string, token: string) => {
      if (!org || !token) return { teams: [], error: null };

      try {
        setLoadingTeamsFromGithub(true);
        setGithubError('');
        setSamlAuthUrl(null);
        const octokit = new Octokit({ auth: token });

        // Paginate through all teams from the selected organization
        const allTeams = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const { data: teams } = await octokit.rest.teams.list({
            org,
            per_page: 100,
            page,
          });

          allTeams.push(...teams);

          // Check if there are more pages
          hasMore = teams.length === 100;
          page++;
        }

        // Map GitHub teams to Owner format matching catalog entities
        const teamOwners: Owner[] = allTeams.map(team => ({
          entityRef: `group:default/${team.slug}`,
          name: team.slug,
          displayName: team.name,
          type: 'team' as const,
          members: [], // GitHub API doesn't return members in list endpoint
        }));

        // Auto-close the SAML popup if the user returned to this tab before closing it.
        if (samlPopupRef.current && !samlPopupRef.current.closed) {
          samlPopupRef.current.close();
        }
        samlPopupRef.current = null;

        return { teams: teamOwners, error: null };
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('Error loading teams from GitHub:', error);

        // Use x-github-sso header as the sole SAML discriminator — GitHub always
        // sets it on SAML-blocked 403s, so message text is not a reliable signal.
        const ssoHeader: string =
          (error?.response?.headers as Record<string, string> | undefined)?.[
            'x-github-sso'
          ] ?? '';
        if (error?.status === 403 && ssoHeader) {
          const ssoUrlMatch = ssoHeader.match(/url=([^\s;]+)/);
          const rawUrl = ssoUrlMatch?.[1] ?? '';
          // Validate the extracted URL to prevent open redirect from a crafted header.
          const ssoUrl = rawUrl.startsWith('https://github.com/')
            ? rawUrl
            : `https://github.com/orgs/${org}/sso`;
          setSamlAuthUrl(ssoUrl);
          const samlError = `⚠️ SAML authorization required for "${org}". Click "Authorize on GitHub" below, then close the popup — teams will reload automatically.`;
          setGithubError(samlError);
          return { teams: [], error: samlError };
        }

        const genericError = `Failed to load teams from GitHub. You may not have access to this organization.`;
        setSamlAuthUrl(null);
        setGithubError(genericError);
        return { teams: [], error: genericError };
      } finally {
        setLoadingTeamsFromGithub(false);
      }
    },
    [],
  );

  // Clears the popup-polling interval when the component unmounts, preventing
  // setState calls on an unmounted component if the popup is still open.
  useEffect(() => {
    return () => {
      if (samlTimerRef.current !== null) {
        window.clearInterval(samlTimerRef.current);
      }
    };
  }, []);

  // Opens the GitHub SAML SSO page as a popup. When the user completes authorization
  // and closes the popup, the same OAuth token is now SAML-authorized — re-run the
  // team listing automatically without requiring a page reload.
  const handleSamlAuthorize = useCallback(() => {
    if (!samlAuthUrl) return;

    const selectedOrg = formContext?.formData?.host?.githubOrg;
    const githubToken = formContext?.formData?.host?.token;
    const orgName = selectedOrg?.split('/').pop();

    const popup = window.open(
      samlAuthUrl,
      'github-saml-auth',
      'width=800,height=600',
    );

    if (!popup) {
      // Popup was blocked — open in new tab as fallback.
      window.open(samlAuthUrl, '_blank');
      return;
    }

    // Store reference so the retry can auto-close the popup on success.
    samlPopupRef.current = popup;

    // Guard against a second click while a popup is already open.
    if (samlTimerRef.current !== null) {
      window.clearInterval(samlTimerRef.current);
    }
    samlTimerRef.current = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(samlTimerRef.current!);
        samlTimerRef.current = null;
        samlPopupRef.current = null;
        // Token is now SAML-authorized — re-load teams for the current org.
        if (orgName && githubToken) {
          loadTeamsFromGithub(orgName, githubToken).then(result => {
            if (result.error === null) {
              setSamlAuthUrl(null);
            }
            if (result.teams.length > 0) {
              setOwners(result.teams);
            }
          });
        }
      }
    }, 500);
  }, [samlAuthUrl, formContext, loadTeamsFromGithub]);

  // Load teams from the Backstage catalog (Groups) — used for local development so
  // the browser does not have to call api.github.com directly (IDP-1050).
  const loadTeamsFromCatalog = useCallback(async () => {
    try {
      setLoadingTeamsFromGithub(true);
      setGithubError('');
      const response = await catalogApi.getEntities({
        filter: { kind: 'Group' },
      });
      const teamOwners: Owner[] = response.items.map(entity => {
        const profile = entity.spec?.profile as
          | { displayName?: string }
          | undefined;
        return {
          entityRef: `group:default/${entity.metadata.name}`,
          name: entity.metadata.name,
          displayName:
            profile?.displayName ||
            (entity.metadata.title as string) ||
            entity.metadata.name,
          type: 'team' as const,
          members: [],
        };
      });
      return { teams: teamOwners, error: null };
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error loading teams from catalog:', error);
      const msg =
        'Failed to load teams from the catalog. The GitHub org sync may not have run yet.';
      setGithubError(msg);
      return { teams: [], error: msg };
    } finally {
      setLoadingTeamsFromGithub(false);
    }
  }, [catalogApi]);

  // Initialize selected owners from formData
  useEffect(() => {
    // Restore from sessionStorage when returning to form
    if (!formData) {
      // Only restore if field is empty
      const savedFormState = sessionStorage.getItem('github-repo-form-state');

      if (savedFormState) {
        try {
          const parsedState = JSON.parse(savedFormState);

          // Check timestamp - don't restore if > 1 hour old
          const ONE_HOUR = 60 * 60 * 1000;
          const isStale =
            parsedState.timestamp &&
            Date.now() - parsedState.timestamp > ONE_HOUR;

          if (!isStale && parsedState.team) {
            onChange(parsedState.team); // Restore team field
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to restore team field:', error);
        }
      }
    }

    if (formData && typeof formData === 'string' && owners.length > 0) {
      try {
        const data = JSON.parse(formData);
        if (Array.isArray(data)) {
          // Multiple owners format
          const selected = data
            .map(item => {
              const ownerName = item.team
                ?.replace('group:default/', '')
                .replace('user:default/', '');
              return owners.find(o => o.name === ownerName);
            })
            .filter((o): o is Owner => !!o);
          // Only update selectedOwners if we found matches OR if selectedOwners is already empty
          // This prevents clearing user selections when owners list is being reloaded
          if (selected.length > 0 || selectedOwners.length === 0) {
            setSelectedOwners(selected);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }
  }, [formData, owners, onChange, selectedOwners.length]);

  // Sync requiredError with rawErrors from scaffolder validation
  // This handles the case when user clicks Review without interacting with the field
  useEffect(() => {
    if (rawErrors && rawErrors.length > 0 && selectedOwners.length === 0) {
      setRequiredError('Team Owner selection is required');
    } else if (selectedOwners.length > 0) {
      setRequiredError('');
    }
  }, [rawErrors, selectedOwners.length]);

  // Load teams from GitHub when organization or repo type changes
  useEffect(() => {
    const loadOrgTeams = async () => {
      // Get selected org and token from form context
      const selectedOrg = formContext?.formData?.host?.githubOrg;
      const githubToken = formContext?.formData?.host?.token;

      // Extract org name from combinedHost format (github.com/orgname -> orgname)
      const orgName = selectedOrg?.split('/').pop();

      const orgChanged = orgName && orgName !== previousOrgRef.current;
      const repoTypeChanged =
        repoType !== previousRepoTypeForTeamLoadRef.current;

      // eslint-disable-next-line no-console
      console.log('👥 TeamPicker check:', {
        org: orgName,
        repoType,
        orgChanged,
        repoTypeChanged,
        previousOrg: previousOrgRef.current,
        previousRepoType: previousRepoTypeForTeamLoadRef.current,
      });

      // Load teams if org changed OR repo type changed
      if (orgName && githubToken && (orgChanged || repoTypeChanged)) {
        if (orgChanged) {
          previousOrgRef.current = orgName;
          // eslint-disable-next-line no-console
          console.log('🔄 Organization changed, loading teams for:', orgName);
        }

        if (repoTypeChanged) {
          previousRepoTypeForTeamLoadRef.current = repoType;
          // eslint-disable-next-line no-console
          console.log('🔄 Repo type changed to:', repoType);
        }

        // Load teams from GitHub API for selected org (or use cached teams if org didn't change)
        let githubTeams = [];

        if (orgChanged) {
          // Org changed - fetch fresh teams. On localhost, read from the catalog
          // (Groups) to avoid a direct browser→GitHub call; otherwise call GitHub.
          const result = isLocalDev
            ? await loadTeamsFromCatalog()
            : await loadTeamsFromGithub(orgName, githubToken);
          githubTeams = result.teams;
        } else {
          // Org didn't change - extract existing teams from current owners
          githubTeams = owners.filter(o => o.type === 'team');
        }

        // For Standard repos: only teams
        if (repoType === 'Standard') {
          setOwners(githubTeams);
        } else {
          // For Playground repos: teams + users from catalog
          try {
            const userEntities = await catalogApi.getEntities({
              filter: { kind: 'User' },
            });

            const userOwners: Owner[] = userEntities.items.map(entity => {
              const profile = entity.spec?.profile as
                | { displayName?: string }
                | undefined;
              return {
                entityRef: `user:default/${entity.metadata.name}`,
                name: entity.metadata.name,
                displayName:
                  profile?.displayName ||
                  (entity.metadata.title as string) ||
                  entity.metadata.name,
                type: 'user' as const,
              };
            });

            setOwners([...githubTeams, ...userOwners]);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error loading users from catalog:', error);
            setOwners(githubTeams);
          }
        }

        // Clear selected owners when org changes (but not when only repo type changes)
        if (orgChanged) {
          setSelectedOwners([]);
          onChange('');
        }
      }
    };

    loadOrgTeams();
  }, [
    formContext?.formData?.host?.githubOrg,
    formContext?.formData?.host?.token,
    repoType,
    catalogApi,
    isLocalDev,
    loadTeamsFromGithub,
    loadTeamsFromCatalog,
    onChange,
    owners,
  ]);

  const handleOwnerChange = (event: SelectChangeEvent<string[]>) => {
    const selectedRefs = event.target.value as string[];
    // Limit to maximum 10 owners (teams or users) for both repo types
    const maxOwners = 10;
    const limitedRefs = selectedRefs.slice(0, maxOwners);
    const newSelected = limitedRefs
      .map(ref => owners.find(o => o.entityRef === ref))
      .filter((o): o is Owner => !!o);

    setSelectedOwners(newSelected);

    if (newSelected.length === 0) {
      onChange('');
      setRequiredError('Team Owner selection is required');
      return;
    }

    // Clear required error when a selection is made
    setRequiredError('');

    // Return array of team references only (no need for individual members since we add teams directly)
    const ownersData = newSelected.map(owner => ({
      team: owner.entityRef,
    }));

    onChange(JSON.stringify(ownersData));
    setOpen(false);
  };

  const maxOwners = 10;
  const maxOwnersReached = selectedOwners.length >= maxOwners;
  const hasErrors = !!requiredError || !!validationError || !!githubError;
  const docsUrl = uiSchema?.['ui:options']?.docsUrl as string | undefined;

  // Separate teams and users for grouped rendering, filtered by search text
  const teamOwners = useMemo(() => {
    const teams = owners.filter(o => o.type === 'team');
    if (!searchText) return teams;
    const lower = searchText.toLowerCase();
    return teams.filter(
      o =>
        o.displayName.toLowerCase().includes(lower) ||
        o.name.toLowerCase().includes(lower),
    );
  }, [owners, searchText]);

  const userOwners = useMemo(() => {
    const users = owners.filter(o => o.type === 'user');
    if (!searchText) return users;
    const lower = searchText.toLowerCase();
    return users.filter(
      o =>
        o.displayName.toLowerCase().includes(lower) ||
        o.name.toLowerCase().includes(lower),
    );
  }, [owners, searchText]);

  return (
    <FormControl
      fullWidth
      className={classes.root}
      error={hasErrors}
      variant="outlined"
    >
      <Box className={classes.labelContainer}>
        <InputLabel shrink sx={{ position: 'relative', transform: 'none' }}>
          {repoType === 'Playground' ? 'Owner' : 'Team Owner'}
          {required ? ' *' : ' (Optional)'}
        </InputLabel>
        {docsUrl && (
          <DocsHelpIcon
            docsUrl={docsUrl}
            tooltipText="Team owner documentation"
          />
        )}
        {loadingTeamsFromGithub && (
          <CircularProgress size={18} sx={{ ml: 1 }} />
        )}
      </Box>

      {githubError && (
        <div style={{ marginBottom: '16px' }}>
          <FormHelperText
            className={classes.errorText}
            style={{
              whiteSpace: 'pre-line',
              marginBottom: samlAuthUrl ? '4px' : '8px',
            }}
          >
            {githubError}
          </FormHelperText>
          {samlAuthUrl && (
            <Button
              size="small"
              variant="text"
              onClick={handleSamlAuthorize}
              disabled={loadingTeamsFromGithub}
              sx={{
                p: 0,
                minWidth: 'auto',
                textTransform: 'none',
                fontSize: '0.875rem',
                verticalAlign: 'baseline',
              }}
            >
              Authorize on GitHub →
            </Button>
          )}
        </div>
      )}

      <Select
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchText('');
        }}
        value={selectedOwners.map(o => o.entityRef)}
        onChange={handleOwnerChange}
        required={required}
        displayEmpty
        MenuProps={{
          autoFocus: false,
          PaperProps: { sx: { maxHeight: 350 } },
        }}
        renderValue={selected => {
          const refs = selected as string[];
          if (refs.length === 0) {
            return (
              <span style={{ color: theme.palette.text.disabled }}>
                {repoType === 'Playground'
                  ? 'Select teams or users...'
                  : 'Select teams...'}
              </span>
            );
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {refs.map(ref => {
                const owner = owners.find(o => o.entityRef === ref);
                return (
                  <Chip
                    key={ref}
                    label={owner ? owner.displayName : ref}
                    size="small"
                    onMouseDown={e => e.stopPropagation()}
                    onDelete={() => {
                      const newSelected = selectedOwners.filter(
                        o => o.entityRef !== ref,
                      );
                      setSelectedOwners(newSelected);
                      if (newSelected.length === 0) {
                        onChange('');
                        setRequiredError('Team Owner selection is required');
                      } else {
                        const ownersData = newSelected.map(o => ({
                          team: o.entityRef,
                        }));
                        onChange(JSON.stringify(ownersData));
                      }
                    }}
                  />
                );
              })}
            </Box>
          );
        }}
      >
        <ListSubheader
          sx={{
            backgroundColor: theme.palette.background.paper,
            pt: 1,
            pb: 1,
          }}
        >
          <TextField
            size="small"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Type to filter..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
          />
        </ListSubheader>
        {teamOwners.length > 0 && userOwners.length > 0 && (
          <ListSubheader disableSticky>Teams</ListSubheader>
        )}
        {teamOwners.map(owner => (
          <MenuItem
            key={owner.entityRef}
            value={owner.entityRef}
            disabled={
              maxOwnersReached &&
              !selectedOwners.some(o => o.entityRef === owner.entityRef)
            }
          >
            {owner.displayName !== owner.name
              ? `${owner.displayName} (${owner.name})`
              : owner.displayName}
          </MenuItem>
        ))}
        {userOwners.length > 0 && (
          <ListSubheader disableSticky>Users</ListSubheader>
        )}
        {userOwners.map(owner => (
          <MenuItem
            key={owner.entityRef}
            value={owner.entityRef}
            disabled={
              maxOwnersReached &&
              !selectedOwners.some(o => o.entityRef === owner.entityRef)
            }
          >
            {owner.displayName !== owner.name
              ? `${owner.displayName} (${owner.name})`
              : owner.displayName}
          </MenuItem>
        ))}
        {teamOwners.length === 0 && userOwners.length === 0 && searchText && (
          <MenuItem disabled>No matches found</MenuItem>
        )}
      </Select>

      {maxOwnersReached && (
        <FormHelperText className={classes.helperText}>
          {repoType === 'Playground'
            ? `Maximum ${maxOwners} teams or individual users can be selected`
            : `Maximum ${maxOwners} teams can be selected`}
        </FormHelperText>
      )}
      {validationError && (
        <FormHelperText className={classes.errorText}>
          {validationError}
        </FormHelperText>
      )}
      {requiredError && (
        <FormHelperText className={classes.errorText}>
          {requiredError}
        </FormHelperText>
      )}
      {!hasErrors && (
        <FormHelperText className={classes.helperText}>
          {repoType === 'Playground' ? (
            schema.description
          ) : (
            <>
              Required for non-Playground repos. The team that owns this
              repository — team members will have write access and PR review
              responsibilities. Loaded live from{' '}
              <a
                href={`https://github.com/orgs/${
                  formContext?.formData?.host?.githubOrg?.split('/').pop() ??
                  'mediwareinc'
                }/teams`}
                target="_blank"
                rel="noopener noreferrer"
              >
                GitHub
              </a>{' '}
              when you select an organization.
            </>
          )}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const TeamPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TeamPicker',
    component: TeamPicker,
    validation: (value: string, validation: FieldValidation) => {
      // Block form submission if no team is selected
      if (!value || value === '' || value === '[]') {
        validation.addError('Team Owner selection is required');
      }
    },
  }),
);
